import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import {
  IoChevronBack,
  IoChevronForward,
  IoLockClosedOutline,
  IoMailOutline,
  IoCallOutline,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoWarningOutline,
} from "react-icons/io5";
import FamilyHeader from "./FamilyHeader";
import FamilyOtpVerifyDialog from "./FamilyOtpVerifyDialog";
import AlertMessage from "../../common/components/AlertMessage";
import { OTP_ACTION_SEND, OTP_PURPOSE_EMAIL_VERIFICATION, OTP_ROLE_FAMILY_MEMBER, sendEmailOtp, sendMobileOtp } from "../../common/api/familyportalOtpApi";
import { getProfileDetails, PROFILE_ROLE_FAMILY_MEMBER } from "../../common/api/profileApi";
import { CONTACT_VERIFICATION_ROLE } from "../../common/utils/contactVerification";
import {
  RESEND_LOCK_MS,
  formatMobileSendLockMessage,
  isSendOtpRateLimitError,
  parseLockedUntilMs,
  readPersistedEmailVerifyResendLock,
  readPersistedMobileResendLock,
  resolveEmailVerifyLockEndsAt,
  writePersistedEmailVerifyResendLock,
  writePersistedMobileResendLock,
  clearPersistedEmailVerifyResendLock,
  clearPersistedMobileResendLock,
} from "../../common/utils/forgotPasswordResendLock";
import {
  canUseProfileResetPassword,
  formatProfileMobile,
  formatProfileResetPasswordLockMessage,
  getFamilyAccountSettings,
  getProfileInitials,
  getProfileResetPasswordLockRemainingSeconds,
  isProfileResetPasswordLocked,
  mapApiProfileToState,
  syncFamilyVerificationFromProfile,
} from "../../common/utils/familyAccountSettings";
import { getStoredUserEmail, hasFamilyClientSelection } from "../../common/utils/sessionContext";

const formatProfileHeroAddress = (profile) => {
  const parts = [
    profile.address1,
    profile.address2,
    [profile.city, profile.state, profile.zipcode].filter(Boolean).join(", "),
  ].filter(Boolean);

  return parts.join(", ");
};

const ProfileHero = ({ profile }) => {
  const displayName = profile.fullName || [profile.firstName, profile.lastName].filter(Boolean).join(" ") || "Family member";
  const initials = getProfileInitials(profile.firstName, profile.lastName);
  const addressLine = formatProfileHeroAddress(profile);
  const isFullyVerified = profile.emailVerified && profile.mobileVerified;

  return (
    <header className={`family-profile-hero${addressLine ? "" : " family-profile-hero--no-address"}`}>
      <div className="family-profile-hero-avatar-wrap">
        <div className="family-profile-hero-avatar" aria-hidden="true">
          {initials}
        </div>
        <span
          className={`family-profile-hero-badge ${isFullyVerified ? "family-profile-hero-badge--verified" : "family-profile-hero-badge--pending"}`}
          aria-label={isFullyVerified ? "Profile verified" : "Verification pending"}
        />
      </div>
      <div className="family-profile-hero-text">
        <h2 className="family-profile-hero-name">{displayName}</h2>
        {addressLine ? <p className="family-profile-hero-address">{addressLine}</p> : null}
      </div>
    </header>
  );
};

const ContactFormField = ({
  id,
  label,
  icon: Icon,
  value,
  isVerified,
  hasValue,
  onVerify,
  verifyDisabled = false,
  isVerifying = false,
  fieldWarning = "",
}) => (
  <div className="family-profile-field family-profile-field--contact">
    <div className="family-profile-field-head">
      <label htmlFor={id} className="family-profile-field-label">
        {Icon ? (
          <span className="family-profile-contact-icon" aria-hidden="true">
            <Icon />
          </span>
        ) : null}
        {label}
      </label>
      <div className="family-profile-field-actions">
        {/*
        {isVerified ? (
          <span className="family-profile-field-status family-profile-field-status--ok" aria-label="Verified">
            <IoCheckmarkCircle aria-hidden="true" /> Verified
          </span>
        ) : null}
        {!isVerified ? (
          <>
            <span className="family-profile-field-status family-profile-field-status--warn" aria-label="Not verified">
              <IoAlertCircle aria-hidden="true" /> Not verified
            </span>
            {hasValue && onVerify ? (
              <button
                type="button"
                className="family-profile-field-action family-profile-field-action--primary"
                onClick={onVerify}
                disabled={verifyDisabled || isVerifying}>
                {isVerifying ? "Sending…" : "Verify"}
              </button>
            ) : null}
          </>
        ) : null}
        */}
      </div>
    </div>
    <input id={id} type="text" className="family-profile-field-input" value={value} readOnly />
    {/*
    {fieldWarning ? (
      <p className="family-profile-field-warning" role="alert">
        <IoWarningOutline aria-hidden="true" />
        {fieldWarning}
      </p>
    ) : null}
    */}
  </div>
);

const FamilySettings = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(() => getFamilyAccountSettings());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [resetBlockedSeconds, setResetBlockedSeconds] = useState(0);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [mobileOtpMeta, setMobileOtpMeta] = useState(null);
  const [emailOtpMeta, setEmailOtpMeta] = useState(null);
  const [isSendingMobileOtp, setIsSendingMobileOtp] = useState(false);
  const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);
  const [mobileSendError, setMobileSendError] = useState("");
  const [emailSendError, setEmailSendError] = useState("");
  const [mobileSendLockEndsAt, setMobileSendLockEndsAt] = useState(null);
  const [emailSendLockEndsAt, setEmailSendLockEndsAt] = useState(null);
  const [mobileSendLockSeconds, setMobileSendLockSeconds] = useState(0);
  const [emailSendLockSeconds, setEmailSendLockSeconds] = useState(0);
  const [verifyToast, setVerifyToast] = useState("");

  const isResetPasswordLocked = isProfileResetPasswordLocked(profile);
  const canResetPassword = canUseProfileResetPassword(profile);
  const displayedResetBlockedSeconds =
    resetBlockedSeconds > 0
      ? resetBlockedSeconds
      : getProfileResetPasswordLockRemainingSeconds(profile);

  const isMobileSendLocked = Boolean(mobileSendLockEndsAt && mobileSendLockEndsAt > Date.now());
  const displayedMobileSendLockSeconds =
    mobileSendLockSeconds > 0
      ? mobileSendLockSeconds
      : mobileSendLockEndsAt
        ? Math.max(0, Math.ceil((mobileSendLockEndsAt - Date.now()) / 1000))
        : 0;
  const mobileSendLockMessage = isMobileSendLocked
    ? formatMobileSendLockMessage(displayedMobileSendLockSeconds)
    : "";

  const isEmailSendLocked = Boolean(emailSendLockEndsAt && emailSendLockEndsAt > Date.now());
  const displayedEmailSendLockSeconds =
    emailSendLockSeconds > 0
      ? emailSendLockSeconds
      : emailSendLockEndsAt
        ? Math.max(0, Math.ceil((emailSendLockEndsAt - Date.now()) / 1000))
        : 0;
  const emailSendLockMessage = isEmailSendLocked
    ? formatMobileSendLockMessage(displayedEmailSendLockSeconds)
    : "";

  const applyMobileSendLock = (lockEndsAt) => {
    if (!lockEndsAt) {
      return;
    }
    setMobileSendLockEndsAt(lockEndsAt);
    writePersistedMobileResendLock(profile.mobile, lockEndsAt);
  };

  const applyEmailSendLock = (lockEndsAt) => {
    if (!lockEndsAt) {
      return;
    }
    setEmailSendLockEndsAt(lockEndsAt);
    writePersistedEmailVerifyResendLock(profile.email, lockEndsAt);
  };

  /*
  const handleEmailVerify = async () => {
    if (!profile.email || isEmailSendLocked || isSendingEmailOtp) {
      return;
    }

    setIsSendingEmailOtp(true);
    setEmailSendError("");
    const result = await sendEmailOtp({
      email: profile.email,
      purpose: OTP_PURPOSE_EMAIL_VERIFICATION,
      action: OTP_ACTION_SEND,
      role: OTP_ROLE_FAMILY_MEMBER,
    });
    setIsSendingEmailOtp(false);

    if (result.success) {
      setEmailOtpMeta(result.data);
      setVerifyTarget("email");
      return;
    }

    if (isSendOtpRateLimitError(result.data)) {
      const lockEndsAt = resolveEmailVerifyLockEndsAt({
        email: profile.email,
        lockedUntil: result.data?.lockedUntil,
        attemptsRemaining: result.data?.attemptsRemaining ?? 0,
      });
      applyEmailSendLock(lockEndsAt);
      return;
    }

    setEmailSendError(result.message || "Failed to send verification code.");
  };

  const handleMobileVerify = async () => {
    if (!profile.mobile || isMobileSendLocked || isSendingMobileOtp) {
      return;
    }

    setIsSendingMobileOtp(true);
    setMobileSendError("");
    const result = await sendMobileOtp({
      mobileNumber: profile.mobile,
      role: OTP_ROLE_FAMILY_MEMBER,
    });
    setIsSendingMobileOtp(false);

    if (result.success) {
      setMobileOtpMeta(result.data);
      setVerifyTarget("mobile");
      return;
    }

    if (isSendOtpRateLimitError(result.data)) {
      const lockEndsAt =
        parseLockedUntilMs(result.data?.lockedUntil) ||
        (result.data?.attemptsRemaining === 0 ? Date.now() + RESEND_LOCK_MS : null);
      applyMobileSendLock(lockEndsAt);
      return;
    }

    setMobileSendError(result.message || "Failed to send verification code.");
  };
  */

  const handleOtpSendRateLimited = (lockEndsAt) => {
    const target = verifyTarget;
    setVerifyTarget(null);
    setMobileOtpMeta(null);
    setEmailOtpMeta(null);

    if (target === "mobile") {
      applyMobileSendLock(lockEndsAt);
      return;
    }

    if (target === "email") {
      applyEmailSendLock(lockEndsAt);
    }
  };

  const handleVerifySuccess = (message) => {
    const target = verifyTarget;
    setVerifyTarget(null);
    setMobileOtpMeta(null);
    setEmailOtpMeta(null);
    setMobileSendError("");
    setEmailSendError("");
    clearPersistedMobileResendLock();
    clearPersistedEmailVerifyResendLock();

    if (target === "mobile") {
      setProfile((prev) => {
        const updated = { ...prev, mobileVerified: true };
        syncFamilyVerificationFromProfile(updated);
        return updated;
      });
      setVerifyToast(message || "Mobile number verified successfully.");
    } else if (target === "email") {
      setProfile((prev) => {
        const updated = { ...prev, emailVerified: true };
        syncFamilyVerificationFromProfile(updated);
        return updated;
      });
      setVerifyToast(message || "Email verified successfully.");
    }
  };

  useEffect(() => {
    const persistedLock = readPersistedMobileResendLock(profile.mobile);
    if (persistedLock) {
      setMobileSendLockEndsAt(persistedLock);
    }
  }, [profile.mobile]);

  useEffect(() => {
    const persistedLock = readPersistedEmailVerifyResendLock(profile.email);
    if (persistedLock) {
      setEmailSendLockEndsAt(persistedLock);
    }
  }, [profile.email]);

  useEffect(() => {
    if (!mobileSendLockEndsAt || mobileSendLockEndsAt <= Date.now()) {
      setMobileSendLockSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((mobileSendLockEndsAt - Date.now()) / 1000));
      setMobileSendLockSeconds(remaining);
      if (remaining === 0) {
        setMobileSendLockEndsAt(null);
        clearPersistedMobileResendLock();
      }
    };

    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [mobileSendLockEndsAt]);

  useEffect(() => {
    if (!emailSendLockEndsAt || emailSendLockEndsAt <= Date.now()) {
      setEmailSendLockSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((emailSendLockEndsAt - Date.now()) / 1000));
      setEmailSendLockSeconds(remaining);
      if (remaining === 0) {
        setEmailSendLockEndsAt(null);
        clearPersistedEmailVerifyResendLock();
      }
    };

    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [emailSendLockEndsAt]);

  useEffect(() => {
    const blockedUntilMs = profile.resetBlockedUntilMs;
    if (!blockedUntilMs || blockedUntilMs <= Date.now()) {
      setResetBlockedSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((blockedUntilMs - Date.now()) / 1000));
      setResetBlockedSeconds(remaining);
      if (remaining === 0) {
        setProfile((prev) => ({
          ...prev,
          enableResetPassword: true,
          resetBlockedUntilMs: null,
        }));
      }
    };

    tick();
    const timerId = window.setInterval(tick, 1000);
    return () => window.clearInterval(timerId);
  }, [profile.resetBlockedUntilMs]);

  useEffect(() => {
    let cancelled = false;

    const fetchProfile = async () => {
      const emailId = getStoredUserEmail();
      if (!emailId) {
        if (!cancelled) {
          setLoadError("Unable to load profile. Please sign in again.");
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setLoadError("");
      const result = await getProfileDetails({
        emailId,
        role: PROFILE_ROLE_FAMILY_MEMBER,
      });

      if (cancelled) {
        return;
      }

      if (!result.success || !result.data) {
        setLoadError(result.message || "Failed to load profile details.");
        setLoading(false);
        return;
      }

      const nextProfile = mapApiProfileToState(result.data);
      syncFamilyVerificationFromProfile(nextProfile);
      setProfile(nextProfile);
      setLoading(false);
    };

    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayEmail = profile.email || "";
  const displayMobile = profile.mobile ? formatProfileMobile(profile.mobile) : "";
  const emailFieldWarning = emailSendLockMessage || emailSendError;
  const mobileFieldWarning = mobileSendLockMessage || mobileSendError;

  return (
    <div className="family-page-layout family-settings-layout">
      {verifyToast ? (
        <AlertMessage
          status={200}
          message={verifyToast}
          origin="center"
          closeAlert={() => setVerifyToast("")}
        />
      ) : null}
      <FamilyHeader />
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
        <img src={require("../../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>
      <div className="family-settings-page family-profile-page">
        <div className="family-checklist-topbar">
          <button
            type="button"
            className="family-checklist-back"
            onClick={() => navigate(hasFamilyClientSelection() ? "/family-home" : "/family-select-client")}
            aria-label="Back">
            <IoChevronBack />
          </button>
          <h1 className="family-checklist-title-center">Profile</h1>
          <span className="family-vitals-topbar-spacer" aria-hidden="true" />
        </div>

        {!loading && loadError ? (
          <p className="family-profile-error" role="alert">
            {loadError}
          </p>
        ) : null}

        {/*
        <FamilyOtpVerifyDialog
          open={Boolean(verifyTarget)}
          onClose={() => setVerifyTarget(null)}
          target={verifyTarget || "email"}
          email={profile.email}
          mobileNumber={profile.mobile}
          contactDisplay={verifyTarget === "mobile" ? displayMobile : displayEmail}
          verificationRole={CONTACT_VERIFICATION_ROLE.FAMILY}
          initialOtpMeta={
            verifyTarget === "mobile" ? mobileOtpMeta : verifyTarget === "email" ? emailOtpMeta : null
          }
          onSendRateLimited={handleOtpSendRateLimited}
          onSuccess={handleVerifySuccess}
        />
        */}

        {!loading && !loadError ? (
          <>
            <ProfileHero profile={profile} />

            <section className="family-profile-form" aria-labelledby="family-profile-contact-heading">
              <h3 id="family-profile-contact-heading" className="family-profile-section-title">
                Contact
              </h3>
              <div className="family-profile-form-card">
                <ContactFormField
                  id="family-profile-email"
                  label="Email"
                  icon={IoMailOutline}
                  value={displayEmail || "—"}
                  isVerified={profile.emailVerified}
                  hasValue={Boolean(displayEmail)}
                  // onVerify={handleEmailVerify}
                  // verifyDisabled={isEmailSendLocked}
                  // isVerifying={isSendingEmailOtp}
                  // fieldWarning={emailFieldWarning}
                />
                <ContactFormField
                  id="family-profile-mobile"
                  label="Mobile number"
                  icon={IoCallOutline}
                  value={displayMobile || "—"}
                  isVerified={profile.mobileVerified}
                  hasValue={Boolean(displayMobile)}
                  // onVerify={handleMobileVerify}
                  // verifyDisabled={isMobileSendLocked}
                  // isVerifying={isSendingMobileOtp}
                  // fieldWarning={mobileFieldWarning}
                />
              </div>
            </section>

            <section className="family-profile-form" aria-labelledby="family-profile-security-heading">
              <h3 id="family-profile-security-heading" className="family-profile-section-title">
                Security
              </h3>
              <button
                type="button"
                className="family-profile-security-row"
                disabled={!canResetPassword}
                onClick={() => {
                  if (canResetPassword) {
                    navigate("/family-reset-password");
                  }
                }}>
                <span className="family-profile-security-icon" aria-hidden="true">
                  <IoLockClosedOutline />
                </span>
                <span className="family-profile-security-copy">
                  <span className="family-profile-security-label">Reset password</span>
                  <span
                    className={`family-profile-security-hint${isResetPasswordLocked ? " family-profile-security-hint--locked" : ""}`}
                    role={isResetPasswordLocked ? "status" : undefined}>
                    {isResetPasswordLocked
                      ? formatProfileResetPasswordLockMessage(displayedResetBlockedSeconds)
                      : "Change your account password"}
                  </span>
                </span>
                <IoChevronForward className="family-profile-security-chevron" aria-hidden="true" />
              </button>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default FamilySettings;
